import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { ComponentType } from '@angular/cdk/overlay';
import { Injectable, inject } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';

import { SnackbarComponent } from '@app/shared/tools/snackbar/snackbar.component';
import { Alert } from '@models/alert.model';
import { AlertType } from '@models/types/all';

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  private snackBar = inject(MatSnackBar);
  config: MatSnackBarConfig = {
    verticalPosition: 'top',
    panelClass: 'mat-mdc-snack-bar-container--alert',
  };

  private alertSubject = new Subject<Alert>();

  constructor() {
    this.getAlerts().subscribe({ next: (alert: Alert) => this.openSnackBar(alert) });
  }

  getAlerts(): Observable<Alert> {
    return this.alertSubject.asObservable();
  }

  getChannelAlerts(channels: string[]): Observable<Alert> {
    return this.getAlerts().pipe(filter((alert: Alert) => channels.includes(alert.channel)));
  }

  /**
   * Display an alert on the page
   */
  notify(alert: Alert) {
    this.alertSubject.next(alert);
  }

  success(message: string, translate = true, autoclose = true) {
    this.alertByType(AlertType.success, message, translate, autoclose);
  }

  error(message: string, translate = true, autoclose = true) {
    this.alertByType(AlertType.error, message, translate, autoclose);
  }

  warning(message: string, translate = true, autoclose = true) {
    this.alertByType(AlertType.warning, message, translate, autoclose);
  }

  openFreeSnackBar<T>(component: ComponentType<any>, config?: MatSnackBarConfig<T>) {
    this.snackBar.openFromComponent(component, config);
  }

  private openSnackBar(alert: Alert) {
    const config = { ...this.config };
    config.duration = alert.autoclose ? alert.duration : 0;
    config.data = alert;

    this.snackBar.openFromComponent(SnackbarComponent, config);
  }

  private alertByType(type: AlertType, message: string, translate = true, autoclose = true) {
    const alert = new Alert({
      type,
      message,
      translate,
      autoclose,
    });

    this.notify(alert);
  }
}
